import React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatbot } from '../../Context/ChatbotContext';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import ChatbotIcon from '../Icons/ChatbotIcon';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '@mui/material/styles';
import AttachmentsDisplay from './AttachmentsDisplay';
import PreviewAttachmentDialog from '../PreviewAttachmentDialog';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { UserDetailsContext } from '../../Context/userDetailsContext';
import { ConfigLinksDetailsContext } from '../../Context/cofingLinksDetailsContext';
import { getAvatarInitials, getAvatarColor } from '../../utils/avatarUtils';


const Chatbot = ({ open, onClose, onOpen }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const isArabic = i18n.language === 'ar';
  const { userDetails } = React.useContext(UserDetailsContext);
  const { configLinks } = React.useContext(ConfigLinksDetailsContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const [attachmentToPreview, setAttachmentToPreview] = useState(null);
  const messagesEndRef = useRef(null);

  const municipalityName = isArabic
    ? (configLinks?.municipalityNameAr?.trim() || '')
    : (configLinks?.municipalityNameEn?.trim() || '');

  const assistantName = isArabic
    ? (configLinks?.municipalityNameAr?.trim() ? `مساعد ${configLinks.municipalityNameAr.trim()} الذكي` : '')
    : (configLinks?.municipalityNameEn?.trim() ? `${configLinks.municipalityNameEn.trim()} Smart Assistant` : '');

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom when drawer opens
  useEffect(() => {
    if (open) {
      // small delay to ensure Drawer content is rendered
      const id = setTimeout(scrollToBottom, 150);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Initialize with welcome message
  useEffect(() => {
    // Only show welcome message if we have the actual municipality name
    const hasRealMunicipalityName = Boolean(configLinks?.municipalityNameAr?.trim() || configLinks?.municipalityNameEn?.trim());

    if (messages.length === 0 && hasRealMunicipalityName) {
      const welcomeMessage = isArabic
        ? `مرحباً! أنا ${assistantName}. كيف يمكنني مساعدتك اليوم؟`
        : `Hello! I am ${assistantName}. How can I help you today?`;

      setMessages([
        {
          id: 1,
          text: welcomeMessage,
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
  }, [isArabic, assistantName, configLinks, messages.length]);

  const handlePreviewAttachment = (attachment) => {
    setAttachmentToPreview(attachment);
    setShowAttachmentPreview(true);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const clientNow = new Date();
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'West Bank Standard Time';
      const response = await axios.post('/Chatbot/chat', {
        message: inputMessage,
        history: messages
          .filter((m) => m.sender !== 'bot' || m.id !== 1)
          .slice(-6) // Limit history to last 6 messages (3 from user, 3 from bot)
          .map((m) => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text,
          })),
      }, {
        headers: {
          'X-Client-Sent-At': clientNow.toISOString(),
          'X-Client-TimeZone': clientTimeZone,
          'X-Client-Utc-Offset-Minutes': String(clientNow.getTimezoneOffset()),
        },
      });

      const payload = response;
      const botMessage = {
        id: messages.length + 2,
        text:
          payload?.response ||
          payload?.Response ||
          payload?.message ||
          (isArabic
            ? 'عذراً، لم يتم تلقي رد. يرجى المحاولة مرة أخرى.'
            : 'No response received. Please try again.'),
        sender: 'bot',
        timestamp: new Date(),
        attachments: payload?.dataItem?.attachments || payload?.attachments,
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);

      // Check if error is due to authentication requirement
      const errorData = error?.response?.data || error;
      let errorText;

      if (errorData?.message && errorData.message.includes('login')) {
        errorText = isArabic
          ? 'هذه المعلومات تتطلب تسجيل الدخول. يرجى تسجيل الدخول أولاً.'
          : 'This information requires login. Please log in first.';
      } else if (errorData?.message) {
        errorText = errorData.message;
      } else {
        errorText = isArabic
          ? 'عذراً، حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى.'
          : 'Sorry, a connection error occurred. Please try again.';
      }

      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickQuestions = isArabic
    ? [
        'ما هي الخدمات المتاحة؟',
        'كيف يمكنني التواصل مع البلدية؟',
        'ما هي طلباتي؟',
        'كيف أقدم طلب؟',
      ]
    : [
        'What services are available?',
        'How can I contact the municipality?',
        'What are my requests?',
        'How do I submit a request?',
      ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  return (
    <Drawer
      anchor={'left'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'absolute',
          width: { xs: '100%', md: 420 },
          height: { xs: '70vh', md: '65vh' },
          boxShadow: `0 24px 48px ${theme.palette.primary.main}25`,
          top: 'auto',
          bottom: { xs: 0, md: 24 },
          left: { xs: 0, md: 90 },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '16px 16px 0 0', md: 7 },
          border: `1px solid ${theme.palette.primary.main}22`,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ height: { xs: '70vh', md: '65vh' }, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar
          position="static"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: 'none',
          }}
        >
          <Toolbar>
             <IconButton color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
      
            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'right',mx: 2 }}>
              {assistantName}
            </Typography>
               <Avatar sx={{ bgcolor: 'transparent', fontSize: 24 , borderRadius:10, border: '1.5px solid white' ,padding:"4px" }}>
              <ChatbotIcon />
            </Avatar>
          </Toolbar>
        </AppBar>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <Box
            sx={{
              p: 2,
              backgroundColor: `${theme.palette.primary.light}1A`,
              borderBottom: `1px solid ${theme.palette.primary.main}1F`,
              direction: isArabic ? 'rtl' : 'ltr',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                mb: 1,
                display: 'block',
                fontWeight: 'bold',
                textAlign: isArabic ? 'right' : 'left',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {isArabic ? 'أسئلة شائعة:' : 'Common Questions:'}
            </Typography>
            <Stack
              direction={isArabic ? 'row-reverse' : 'row'}
              spacing={1}
              flexWrap="wrap"
              gap={1}
              sx={{
                justifyContent: isArabic ? 'flex-end' : 'flex-start',
                direction: isArabic ? 'rtl' : 'ltr'
              }}
            >
              {quickQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  onClick={() => handleQuickQuestion(question)}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: `${theme.palette.primary.main}14`,
                    color: theme.palette.primary.dark,
                    border: `1px solid ${theme.palette.primary.main}22`,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            background: `linear-gradient(180deg, ${theme.palette.primary.light}14 0%, ${theme.palette.background.default} 70%)`,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? (isArabic ? 'flex-start' : 'flex-end') : (isArabic ? 'flex-end' : 'flex-start'),
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? (isArabic ? 'row' : 'row-reverse') : (isArabic ? 'row-reverse' : 'row'),
                  alignItems: 'flex-start',
                  maxWidth: '80%',
                }}
              >
                <Avatar
                  src={
                    message.sender === 'user' && userDetails?.isAuthenticated
                      ? (userDetails?.citizen?.profilePicturePreview ||
                        resolveMediaUrl(userDetails?.citizen?.profilePicture, { attachmentRoot: true }))
                      : undefined
                  }
                  sx={{
                    bgcolor: message.sender === 'user' 
                      ? (userDetails?.isAuthenticated 
                          ? getAvatarColor(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`)
                          : 'primary.main'
                        )
                      : 'secondary.main',
                    width: 34,
                    height: 34,
                    mx: 1,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  {message.sender === 'user'
                    ? (userDetails?.isAuthenticated
                        ? (!(userDetails?.citizen?.profilePicture || userDetails?.citizen?.profilePicturePreview) &&
                            getAvatarInitials(`${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`))
                        : <UserIcon fontSize="small" />
                      )
                    : <ChatbotIcon  />
                  }
                </Avatar>
                <Paper
                  sx={{
                    py: 0.25,
                    px: 1.5,
                    backgroundColor: message.sender === 'user' ? 'primary.main' : theme.palette.background.paper,
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: 4,
                    direction: isArabic ? 'rtl' : 'ltr',
                    textAlign: isArabic ? 'right' : 'left',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    border: message.sender === 'user' ? 'none' : `1px solid ${theme.palette.primary.main}1A`,
                    boxShadow: message.sender === 'user'
                      ? `0 8px 18px ${theme.palette.primary.main}36`
                      : `0 6px 14px ${theme.palette.primary.main}14`,
                  }}
                >
                  <Box
                    sx={{
                      '& p': { margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& ul, & ol': { paddingInlineStart: '1.5em', margin: '0.5em 0', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left' },
                      '& li': { marginBottom: '0.25em', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& h1, & h2, & h3': { margin: '0.5em 0', fontWeight: 'bold', direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left', wordWrap: 'break-word' },
                      '& strong': { fontWeight: 'bold' },
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      direction: isArabic ? 'rtl' : 'ltr',
                      textAlign: isArabic ? 'right' : 'left',
                      fontFamily: isArabic ? '"Noto Sans Arabic", "Segoe UI", Roboto, sans-serif' : '"Segoe UI", Roboto, sans-serif',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => {
                          if (href && href.startsWith('/')) {
                            return (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(href);
                                }}
                                sx={{
                                  mx: 0.5,
                                  my: 0.3,
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontSize: '0.8rem',
                                  boxShadow: 'none',
                                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                                }}
                              >
                                {children}
                              </Button>
                            );
                          }
                          return <a href={href}>{children}</a>;
                        },
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                    {message.attachments && message.attachments.length > 0 && (
                      <AttachmentsDisplay
                        attachments={message.attachments}
                        onPreview={handlePreviewAttachment}
                      />
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: isArabic ? 'flex-end' : 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, mx: 1 }}>
                  <ChatbotIcon fontSize="small" />
                </Avatar>
                <Paper
                  sx={{
                    p: 1.25,
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.primary.main}25`,
                    boxShadow: `0 8px 16px ${theme.palette.primary.main}14`,
                    backgroundColor: `${theme.palette.primary.light}12`,
                  }}
                >
                  <Stack direction={isArabic ? 'row-reverse' : 'row'} spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '& span': {
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          display: 'inline-block',
                          backgroundColor: theme.palette.primary.main,
                          opacity: 0.4,
                          animation: 'chatbotPulse 1.2s infinite ease-in-out',
                        },
                        '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                        '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                        '@keyframes chatbotPulse': {
                          '0%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
                          '50%': { opacity: 1, transform: 'translateY(-2px)' },
                        },
                      }}
                    >
                      <span />
                      <span />
                      <span />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {isArabic ? 'يفكر...' : 'Thinking...'}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Paper
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: `${theme.palette.background.paper}`,
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isArabic ? 'اكتب رسالتك هنا...' : 'Type your message here...'}
              FormHelperTextProps={{
                sx: {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                }
              }}
              variant="outlined"
              size="small"
              dir={isArabic ? 'rtl' : 'ltr'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                },
                '& .MuiInputBase-input': {
                  textAlign: isArabic ? 'right' : 'left',
                  direction: isArabic ? 'rtl' : 'ltr',
                },
              }}
            />
            <IconButton color="primary" onClick={handleSendMessage} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Stack>
        </Paper>
        {showAttachmentPreview && (
          <PreviewAttachmentDialog
            open={showAttachmentPreview}
            handleClose={() => setShowAttachmentPreview(false)}
            fileData={attachmentToPreview}
          />
        )}
      </Box>
    </Drawer>
  );
};

// Floating Button Component
export const ChatbotButton = () => {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { chatbotSettings } = useChatbot();
  const theme = useTheme();

  // Broadcast open state changes so other floating UI can react (e.g., hide when chatbot is open)
  useEffect(() => {
    const event = new CustomEvent('chatbot-open-changed', { detail: { open } });
    window.dispatchEvent(event);
  }, [open]);

  // Don't render the button if chatbot is disabled
  if (!chatbotSettings.enabled) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label="chatbot"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 22,
          left: 20,
          zIndex: 1000,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
            transform: 'scale(1.1)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <ChatbotIcon sx={{ fontSize: 28 }} />
      </Fab>
      <Chatbot open={open} onClose={() => setOpen(false)} onOpen={() => setOpen(true)} />
    </>
  );
};

export default Chatbot;
